import { IStore, getAllStores, getStore } from "@/api/store";
import { useAuth } from "@/components/context/useAuth";
import { Box, Stack, Toolbar, Typography } from "@mui/material";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomUrlCard } from "./components/CustomUrlCard";
import { InvoiceTable } from "./components/InvoiceTable";
import { getProfileStores, getSalesStores } from "@/api/profile";

export const StoreInvoiceDashboard = () => {
    const { user } = useAuth();
    const [getProfileStoresState, getProfileStoresActions] =
        useAsync(getProfileStores);
    const [getStoresState, getStoresActions] = useAsync(getAllStores);
    const [getSalesStoresState, getSalesStoresActions] =
        useAsync(getSalesStores);
    const [currentStore, setCurrentStore] = useState<IStore>();
    const [stores, setStores] = useState<IStore[]>();
    const { store_id } = useParams();

    useMountEffect(() => {
        getStoresActions.execute();
        getProfileStoresActions.execute();
        if (user?.is_sales) {
            getSalesStoresActions.execute();
        }
    });

    useEffect(() => {
        const syncCurrentStore = async () => {
            const storeId = Number(store_id);
            if (storeId && stores) {
                const selectedStore = stores.find(
                    (store) => store.id === storeId
                );
                if (selectedStore) {
                    setCurrentStore(selectedStore);
                } else {
                    // Store not in current list, fetch it directly
                    try {
                        const fetchedStore = await getStore(storeId);
                        setCurrentStore(fetchedStore);
                    } catch {
                        setCurrentStore(undefined);
                    }
                }
            }
        };
        syncCurrentStore();
    }, [store_id, stores]);

    useEffect(() => {
        if (user?.is_admin) {
            if (getStoresState.status === "success" && getStoresState.result) {
                setStores(getStoresState.result.items);
            }
        } else if (user?.is_sales) {
            if (
                getSalesStoresState.status === "success" &&
                getSalesStoresState.result &&
                getProfileStoresState.status === "success" &&
                getProfileStoresState.result
            ) {
                const salesStores = getSalesStoresState.result.items;
                const memberStores = getProfileStoresState.result.items;
                const storeMap = new Map();
                [...salesStores, ...memberStores].forEach((store) => {
                    storeMap.set(store.id, store);
                });
                const allStores = Array.from(storeMap.values());
                setStores(allStores);
            }
        } else {
            if (
                getProfileStoresState.status === "success" &&
                getProfileStoresState.result
            ) {
                setStores(getProfileStoresState.result.items);
            }
        }
    }, [
        getStoresState,
        getProfileStoresState,
        getSalesStoresState.result,
        getSalesStoresState.status,
        user,
    ]);

    if (!stores || !currentStore) {
        return (
            <Box
                sx={{
                    backgroundColor: "#F4F7FE",
                    minHeight: "100vh",
                    width: "100%",
                }}
            >
                <Box sx={{ p: "1em" }}>
                    <Typography
                        sx={{
                            color: "#2B3674",
                            fontWeight: "600",
                            fontSize: "1.5em",
                        }}
                    >
                        No Stores Found
                    </Typography>
                </Box>
            </Box>
        );
    }

    const base_url = window.location.origin;

    const store_url = `${base_url}/stores/${store_id}/shipping-form`;
    if (!currentStore.is_using_invoice) {
        return (
            <>
                <Typography>Store does not use invoices.</Typography>
            </>
        );
    }
    return (
        <Box
            sx={{
                backgroundColor: "#F4F7FE",
                minHeight: "100vh",
                width: "100%",
                overflowX: "auto",
            }}
        >
            {/* Spacing for mobile app bar */}
            <Toolbar sx={{ display: { sm: "none" } }} />

            <Box sx={{ p: "0 1em 1em 1em" }}>
                <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    paddingTop={4}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            color: "#2B3674",
                            fontWeight: "600",
                        }}
                    >
                        Store Invoices{currentStore && `: ${currentStore.name}`}
                    </Typography>
                </Stack>
                <Box sx={{ display: "flex" }}>
                    <CustomUrlCard url={store_url} />
                </Box>
            </Box>
            <InvoiceTable currentStoreId={currentStore.id} />
        </Box>
    );
};

export default StoreInvoiceDashboard;
